/**
 * Comprehensive logging utility for MedFast Pharmacy Platform
 * Provides structured logging with different levels and audit trail support
 */

export enum LogLevel {
  DEBUG = 0,
  INFO = 1,
  WARN = 2,
  ERROR = 3,
  AUDIT = 4
}

export interface LogEntry {
  timestamp: string;
  level: LogLevel;
  message: string;
  context?: Record<string, any>;
  userId?: string;
  sessionId?: string;
  action?: string;
  entityType?: string;
  entityId?: string;
  branchId?: string;
  tenantId?: string;
  stackTrace?: string;
}

export interface AuditLogEntry extends LogEntry {
  level: LogLevel.AUDIT;
  action: string;
  entityType: string;
  entityId: string;
  previousValue?: any;
  newValue?: any;
  reason?: string;
  witnessId?: string;
}

class Logger {
  private static instance: Logger;
  private logLevel: LogLevel = LogLevel.INFO;
  private logs: LogEntry[] = [];
  private maxLogs: number = 1000;
  private sessionId: string;

  private constructor() {
    this.sessionId = this.generateSessionId();
    this.logLevel = this.getLogLevelFromEnv();
    
    // Initialize performance monitoring
    this.initPerformanceMonitoring();
  }

  public static getInstance(): Logger {
    if (!Logger.instance) {
      Logger.instance = new Logger();
    }
    return Logger.instance;
  }

  private generateSessionId(): string {
    return `session_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`;
  }

  private getLogLevelFromEnv(): LogLevel {
    const envLevel = process.env.REACT_APP_LOG_LEVEL?.toUpperCase();
    switch (envLevel) {
      case 'DEBUG': return LogLevel.DEBUG;
      case 'INFO': return LogLevel.INFO;
      case 'WARN': return LogLevel.WARN;
      case 'ERROR': return LogLevel.ERROR;
      case 'AUDIT': return LogLevel.AUDIT;
      default: return process.env.NODE_ENV === 'development' ? LogLevel.DEBUG : LogLevel.INFO;
    }
  }

  private initPerformanceMonitoring(): void {
    // Monitor page load performance
    if (typeof window !== 'undefined' && 'performance' in window) {
      window.addEventListener('load', () => {
        setTimeout(() => {
          const perfData = performance.getEntriesByType('navigation')[0] as PerformanceNavigationTiming;
          this.info('Page Load Performance', {
            loadTime: perfData.loadEventEnd - perfData.loadEventStart,
            domContentLoaded: perfData.domContentLoadedEventEnd - perfData.domContentLoadedEventStart,
            firstPaint: this.getFirstPaint(),
            firstContentfulPaint: this.getFirstContentfulPaint()
          });
        }, 0);
      });
    }
  }

  private getFirstPaint(): number | null {
    const paintEntries = performance.getEntriesByType('paint');
    const firstPaint = paintEntries.find(entry => entry.name === 'first-paint');
    return firstPaint ? firstPaint.startTime : null;
  }

  private getFirstContentfulPaint(): number | null {
    const paintEntries = performance.getEntriesByType('paint');
    const firstContentfulPaint = paintEntries.find(entry => entry.name === 'first-contentful-paint');
    return firstContentfulPaint ? firstContentfulPaint.startTime : null;
  }

  private createLogEntry(
    level: LogLevel,
    message: string,
    context?: Record<string, any>,
    additionalFields?: Partial<LogEntry>
  ): LogEntry {
    const entry: LogEntry = {
      timestamp: new Date().toISOString(),
      level,
      message,
      context,
      sessionId: this.sessionId,
      ...additionalFields
    };

    // Add stack trace for errors
    if (level === LogLevel.ERROR) {
      entry.stackTrace = new Error().stack;
    }

    return entry;
  }

  private shouldLog(level: LogLevel): boolean {
    return level >= this.logLevel;
  }

  private persistLog(entry: LogEntry): void {
    this.logs.push(entry);
    
    // Maintain log size limit
    if (this.logs.length > this.maxLogs) {
      this.logs = this.logs.slice(-this.maxLogs);
    }

    // Console output in development
    if (process.env.NODE_ENV === 'development') {
      const logMethod = this.getConsoleMethod(entry.level);
      logMethod(`[${LogLevel[entry.level]}] ${entry.message}`, entry.context || '');
    }

    // Send to external logging service in production
    if (process.env.NODE_ENV === 'production' && entry.level >= LogLevel.WARN) {
      this.sendToExternalLogger(entry);
    }

    // Store audit logs separately
    if (entry.level === LogLevel.AUDIT) {
      this.persistAuditLog(entry as AuditLogEntry);
    }
  }

  private getConsoleMethod(level: LogLevel): (...args: any[]) => void {
    switch (level) {
      case LogLevel.DEBUG: return console.debug;
      case LogLevel.INFO: return console.info;
      case LogLevel.WARN: return console.warn;
      case LogLevel.ERROR: return console.error;
      case LogLevel.AUDIT: return console.info;
      default: return console.log;
    }
  }

  private async sendToExternalLogger(entry: LogEntry): Promise<void> {
    try {
      // In a real implementation, this would send to your logging service
      // For now, we'll store in localStorage as a fallback
      const existingLogs = JSON.parse(localStorage.getItem('medfast_error_logs') || '[]');
      existingLogs.push(entry);
      localStorage.setItem('medfast_error_logs', JSON.stringify(existingLogs.slice(-100)));
    } catch (error) {
      console.error('Failed to persist log entry:', error);
    }
  }

  private persistAuditLog(entry: AuditLogEntry): void {
    try {
      const auditLogs = JSON.parse(localStorage.getItem('medfast_audit_logs') || '[]');
      auditLogs.push(entry);
      localStorage.setItem('medfast_audit_logs', JSON.stringify(auditLogs.slice(-500)));
    } catch (error) {
      console.error('Failed to persist audit log:', error);
    }
  }

  // Public logging methods
  public debug(message: string, context?: Record<string, any>): void {
    if (this.shouldLog(LogLevel.DEBUG)) {
      const entry = this.createLogEntry(LogLevel.DEBUG, message, context);
      this.persistLog(entry);
    }
  }

  public info(message: string, context?: Record<string, any>): void {
    if (this.shouldLog(LogLevel.INFO)) {
      const entry = this.createLogEntry(LogLevel.INFO, message, context);
      this.persistLog(entry);
    }
  }

  public warn(message: string, context?: Record<string, any>): void {
    if (this.shouldLog(LogLevel.WARN)) {
      const entry = this.createLogEntry(LogLevel.WARN, message, context);
      this.persistLog(entry);
    }
  }

  public error(message: string, error?: Error, context?: Record<string, any>): void {
    if (this.shouldLog(LogLevel.ERROR)) {
      const entry = this.createLogEntry(LogLevel.ERROR, message, {
        ...context,
        error: error ? {
          name: error.name,
          message: error.message,
          stack: error.stack
        } : undefined
      });
      this.persistLog(entry);
    }
  }

  public audit(auditData: Omit<AuditLogEntry, 'timestamp' | 'level' | 'sessionId'>): void {
    const entry: AuditLogEntry = {
      ...auditData,
      timestamp: new Date().toISOString(),
      level: LogLevel.AUDIT,
      sessionId: this.sessionId
    };
    this.persistLog(entry);
  }

  // Performance monitoring
  public measurePerformance<T>(
    operation: string,
    fn: () => T | Promise<T>,
    context?: Record<string, any>
  ): T | Promise<T> {
    const startTime = performance.now();
    
    try {
      const result = fn();
      
      if (result instanceof Promise) {
        return result.then(
          (value) => {
            const endTime = performance.now();
            this.info(`Performance: ${operation}`, {
              duration: endTime - startTime,
              ...context
            });
            return value;
          },
          (error) => {
            const endTime = performance.now();
            this.error(`Performance: ${operation} failed`, error as Error, {
              duration: endTime - startTime,
              ...context
            });
            throw error;
          }
        );
      } else {
        const endTime = performance.now();
        this.info(`Performance: ${operation}`, {
          duration: endTime - startTime,
          ...context
        });
        return result;
      }
    } catch (error) {
      const endTime = performance.now();
      this.error(`Performance: ${operation} failed`, error as Error, {
        duration: endTime - startTime,
        ...context
      });
      throw error;
    }
  }

  // Utility methods
  public getLogs(level?: LogLevel): LogEntry[] {
    if (level !== undefined) {
      return this.logs.filter(log => log.level === level);
    }
    return [...this.logs];
  }

  public getAuditLogs(): AuditLogEntry[] {
    try {
      return JSON.parse(localStorage.getItem('medfast_audit_logs') || '[]');
    } catch {
      return [];
    }
  }

  public clearLogs(): void {
    this.logs = [];
    localStorage.removeItem('medfast_error_logs');
    localStorage.removeItem('medfast_audit_logs');
  }

  public exportLogs(): string {
    return JSON.stringify({
      sessionId: this.sessionId,
      exportTime: new Date().toISOString(),
      logs: this.logs,
      auditLogs: this.getAuditLogs()
    }, null, 2);
  }

  // Context setters for user session
  public setUserContext(userId: string, branchId?: string, tenantId?: string): void {
    this.info('User context set', { userId, branchId, tenantId });
  }
}

// Export singleton instance
export const logger = Logger.getInstance();

// Convenience functions
export const log = {
  debug: (message: string, context?: Record<string, any>) => logger.debug(message, context),
  info: (message: string, context?: Record<string, any>) => logger.info(message, context),
  warn: (message: string, context?: Record<string, any>) => logger.warn(message, context),
  error: (message: string, error?: Error, context?: Record<string, any>) => logger.error(message, error, context),
  audit: (auditData: Omit<AuditLogEntry, 'timestamp' | 'level' | 'sessionId'>) => logger.audit(auditData),
  measure: <T>(operation: string, fn: () => T | Promise<T>, context?: Record<string, any>) => 
    logger.measurePerformance(operation, fn, context)
};
